import React, { useEffect, useMemo, useRef, useState } from 'react';
import EChart from '../../../component/chart/echarts/EChart';
import { buildPieOption } from '../../../component/chart/echarts/pieOption';
import { formatBytes, IngestCategoryTotal } from './ingest';

interface IngestTotalsProps {
    totals: IngestCategoryTotal[];
    periodLabel: string;
    palette: string[];
}

type TotalMode = 'lifetime' | 'period';

// Below this card-body width a side legend (~160px) would overlap the ring,
// so the legend moves underneath and the chart grows to fit it.
const SIDE_LEGEND_MIN_WIDTH = 340;
const SCROLLBAR_ALLOWANCE = 20;
const CHART_HEIGHT = 220;
const STACKED_RING_HEIGHT = 170;
const LEGEND_ROW_HEIGHT = 22;

const IngestTotals: React.FC<IngestTotalsProps> = ({ totals, periodLabel, palette }) => {
    const [mode, setMode] = useState<TotalMode>('period');
    const bodyRef = useRef<HTMLDivElement>(null);
    const [stacked, setStacked] = useState(false);

    useEffect(() => {
        const element = bodyRef.current;
        if (!element || typeof ResizeObserver === 'undefined') {
            return;
        }
        // Switching layout changes the chart height, which can toggle the page
        // scrollbar and so this width - defer the update a frame (avoids the
        // "ResizeObserver loop" error) and only switch back once clear of the
        // threshold by more than a scrollbar's width, so it can't oscillate.
        let frame = 0;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const width = element.clientWidth;
                setStacked(current => (current
                    ? width < SIDE_LEGEND_MIN_WIDTH + SCROLLBAR_ALLOWANCE
                    : width < SIDE_LEGEND_MIN_WIDTH));
            });
        });
        observer.observe(element);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, []);

    const { option, height } = useMemo(() => {
        const slices = totals.map((total, index) => {
            const value = mode === 'lifetime' ? total.lifetimeBytes : total.periodBytes;
            const formatted = formatBytes(value);
            return {
                name: total.label,
                value,
                color: palette[index % palette.length],
                formatted: `${formatted.value} ${formatted.unit}`,
            };
        }).filter(slice => slice.value > 0);
        const pie = buildPieOption({ slices, valueLabel: mode === 'lifetime' ? 'Since JVM restart' : periodLabel });
        const originalSeries = (pie as any).series[0];
        const option = {
            ...pie,
            legend: {
                show: true,
                type: 'scroll',
                orient: 'vertical',
                ...(stacked
                    ? { left: 'center', top: STACKED_RING_HEIGHT }
                    : { right: 8, width: 160, top: 'middle' }),
                textStyle: { fontSize: 11 },
                formatter: (name: string) => {
                    const slice = slices.find(item => item.name === name);
                    return slice ? `${name}  ${slice.formatted}` : name;
                },
            },
            series: [{
                ...originalSeries,
                center: stacked ? ['50%', STACKED_RING_HEIGHT / 2] : ['34%', '50%'],
                radius: stacked ? [STACKED_RING_HEIGHT * 0.25, STACKED_RING_HEIGHT * 0.42] : ['42%', '72%'],
                label: { show: false },
                labelLine: { show: false },
                selectedMode: false,
            }],
        };
        const height = stacked ? STACKED_RING_HEIGHT + slices.length * LEGEND_ROW_HEIGHT + 8 : CHART_HEIGHT;
        return { option, height };
    }, [mode, palette, periodLabel, stacked, totals]);

    return (
        <div className="card self-monitor-chart self-monitor-ingest-totals">
            <div className="card-header d-flex align-items-center justify-content-between">
                <span><i className="fal fa-scale-balanced fa-fw" aria-hidden="true" /> Data Ingest Totals</span>
                <div className="btn-group btn-group-sm" role="group" aria-label="Data ingest total period">
                    <button type="button" className={`btn ${mode === 'lifetime' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        aria-pressed={mode === 'lifetime'} onClick={() => setMode('lifetime')}>Since JVM restart</button>
                    <button type="button" className={`btn ${mode === 'period' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        aria-pressed={mode === 'period'} onClick={() => setMode('period')}>{periodLabel}</button>
                </div>
            </div>
            <div className="card-body" ref={bodyRef}>
                {option && totals.some(total => (mode === 'lifetime' ? total.lifetimeBytes : total.periodBytes) > 0)
                    ? <EChart option={option} height={height} />
                    : <div className="text-muted">No ingest totals available.</div>}
            </div>
        </div>
    );
};

export default IngestTotals;
